import clsx from 'clsx';
import { useEffect } from 'react';
import { useSgwtTranslation } from '../../../common/i18n';
import { useBotConversation } from '../shared/BotConversationContext';
import type { SgwtChatbotConversationItem, SuggestedMessage } from '../shared/chatbot.types';
import { enableCopyButtons } from '../shared/chatbot.utils';
import { DisplayError } from './elements/DisplayError';
import { OneMessage } from './elements/OneMessage';
import { UserInteractionContactUsFeedback } from './elements/UserInteractionContactUsFeedback';
import { UserReaction } from './elements/UserReaction';
import { AIAnimatedIcon } from './layout/AIIcon';
import 'highlight.js/styles/atom-one-light.min.css';
import { DisplayInformation } from './elements/DisplayInformation';

interface ConversationProps {
  suggestions?: SuggestedMessage[];
}

export const Conversation = ({ suggestions }: ConversationProps) => {
  const { displayContactUsButton, messages, waitingBot, sendMessage } = useBotConversation();
  const { translate } = useSgwtTranslation();

  // When new messages arrives, scroll down to the last message and enable the "copy" button on code blocks.
  useEffect(() => {
    if (messages.length === 0) {
      return;
    }
    const container = document.querySelector<HTMLDivElement>('.sgwt-chatbot-messages');
    const lastMessage = document.querySelector<HTMLDivElement>('.sgwt-chatbot-messages .sgwt-chatbot-message:last-child');
    if (container) {
      container.scrollTo({
        top: lastMessage ? lastMessage.offsetTop : container.scrollHeight,
        behavior: 'smooth',
      });
    }

    enableCopyButtons();
  }, [messages]);

  if (messages.length === 0) {
    const showSuggestions = suggestions && suggestions.length > 0;

    return (
      <div className="sgwt-chatbot d-flex flex-column h-100">
        <AIAnimatedIcon />
        {showSuggestions && (
          <section className="sgwt-chatbot-suggestions">
            <ul className="list-group">
              {suggestions.map((suggestion) => (
                <li key={suggestion.text} className="list-group-item border-bottom border-opacity-40 p-0">
                  <button
                    type="button"
                    className="btn btn-link btn-icon-start w-100 p-3 justify-content-start gap-2"
                    onClick={() => sendMessage(suggestion.text)}
                  >
                    <i className={clsx('icon', suggestion.outlined && 'icon-outlined')}>{suggestion.icon ?? 'try'}</i>
                    <span>{suggestion.text}</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    );
  }

  return (
    <div className="d-flex flex-column mt-auto mb-n3">
      {messages.map((message) => (
        <ConversationSection key={message._id} item={message} />
      ))}
      {displayContactUsButton && <UserInteractionContactUsFeedback />}

      {waitingBot && (
        <section className="sgwt-chatbot-message d-flex py-2 mb-3">
          <AIAnimatedIcon small />
          <span className="ms-2 ml-2 badge badge-discreet-secondary rounded-pill">{translate('ai.thinking')}</span>
        </section>
      )}
    </div>
  );
};

const ConversationSection = ({ item }: { item: SgwtChatbotConversationItem }) => {
  switch (item.type) {
    case 'message':
      return <OneMessage record={item} />;
    case 'information':
      return <DisplayInformation info={item} />;
    case 'user-feedback':
      return <UserReaction action={item} />;
    case 'error':
      return <DisplayError issue={item} />;
    default:
      return null;
  }
};
